// frontend/src/pages/Testimonials.jsx
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faStarHalfAlt,
  faQuoteLeft,
  faUserCircle,
  faTractor,
  faStore,
  faFilter,
  faSearch,
  faThumbsUp,
  faCalendarAlt,
  faCheckCircle,
  faArrowRight,
  faSpinner,
  faSeedling,
  faGlobe,
  faShip,
  faBuilding,
  faClock,
  faUsers,
  faStar as faStarSolid,
} from "@fortawesome/free-solid-svg-icons";
import { getApprovedTestimonials, markTestimonialHelpful } from "../api";
import { useAuth } from "../context/AuthContext";
import Loader from "../components/UI/Loader";
import Button from "../components/UI/Button";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import SEO from "../components/SEO/SEO";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// HELPERS
// ============================================
const renderStars = (rating) => {
  const stars = [];
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 !== 0;

  for (let i = 0; i < fullStars; i++) {
    stars.push(
      <FontAwesomeIcon
        key={`full-${i}`}
        icon={faStarSolid}
        className="star filled"
      />,
    );
  }
  if (hasHalfStar) {
    stars.push(
      <FontAwesomeIcon
        key="half"
        icon={faStarHalfAlt}
        className="star half-filled"
      />,
    );
  }
  const emptyStars = 5 - stars.length;
  for (let i = 0; i < emptyStars; i++) {
    stars.push(
      <FontAwesomeIcon
        key={`empty-${i}`}
        icon={faStarSolid}
        className="star empty"
      />,
    );
  }
  return stars;
};

const getUserIcon = (userType) => {
  return userType === "farmer" ? faTractor : faStore;
};

const getUserTypeLabel = (userType) => {
  return userType === "farmer" ? "🌾 Farmer" : "🛒 Customer";
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// ============================================
// MAIN COMPONENT
// ============================================
const Testimonials = () => {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [helping, setHelping] = useState({});

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    fetchTestimonials();
  }, [filter, sortBy, searchTerm, page]);

  const fetchTestimonials = async () => {
    setLoading(true);
    try {
      const params = {
        limit: 9,
        page,
      };
      if (filter !== "all") params.userType = filter;
      if (searchTerm) params.search = searchTerm;

      const { data } = await getApprovedTestimonials(params);
      if (page === 1) {
        setTestimonials(data.testimonials || []);
      } else {
        setTestimonials((prev) => [...prev, ...(data.testimonials || [])]);
      }
      setStats(data.stats || {});
      setHasMore(data.testimonials?.length === 9);
    } catch (error) {
      console.error("Error fetching testimonials:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // HANDLERS
  // ============================================
  const handleMarkHelpful = async (testimonialId) => {
    if (!user) {
      alert("Please login to mark testimonials as helpful");
      return;
    }
    setHelping((prev) => ({ ...prev, [testimonialId]: true }));
    try {
      const { data } = await markTestimonialHelpful(testimonialId);
      setTestimonials((prev) =>
        prev.map((t) =>
          t._id === testimonialId
            ? { ...t, helpfulCount: data.helpfulCount }
            : t,
        ),
      );
    } catch (error) {
      console.error("Error marking helpful:", error);
    } finally {
      setHelping((prev) => ({ ...prev, [testimonialId]: false }));
    }
  };

  const loadMore = () => {
    setPage((prev) => prev + 1);
  };

  // ============================================
  // RENDER
  // ============================================
return (
  <>
    <SEO 
      title="Testimonials - Customer Reviews for Kenali Enterprise"
      description="Read testimonials from satisfied farmers and customers who use Kenali Enterprise premium livestock feed supplements and animal nutrition products."
      keywords="testimonials, customer reviews, livestock feed reviews, Kenali Enterprise, farmer testimonials"
      canonical="https://kenalienterprise.com/testimonials"
    />
<motion.div
      className="testimonials-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* ========== HERO ========== */}
      <section className="testimonials-hero">
        <div className="testimonials-hero__overlay" />
        <div className="container testimonials-hero__content">
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <FontAwesomeIcon
              icon={faQuoteLeft}
              className="testimonials-hero__icon"
            />
            <h1>Customer Testimonials</h1>
            <p>
              Read what our farmers and customers have to say about their
              experience with Kenali Enterprise. We source our premium products
              from trusted global partners to bring you the best quality.
            </p>
            <div className="hero-stats">
              <div className="hero-stat">
                <span className="stat-value">{stats.total || 0}+</span>
                <span className="stat-label">Reviews</span>
              </div>
              <div className="hero-stat">
                <span className="stat-value">
                  {stats.averageRating ? stats.averageRating.toFixed(1) : "0"}
                </span>
                <span className="stat-label">Average Rating</span>
              </div>
              <div className="hero-stat">
                <span className="stat-value">⭐</span>
                <span className="stat-label">5 Star Rating</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ========== MAIN CONTENT ========== */}
      <div className="container testimonials-container">
        {/* ========== FILTERS ========== */}
        <div className="testimonials-filters">
          <div className="filter-group">
            <FontAwesomeIcon icon={faFilter} className="filter-icon" />
            <select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All Reviews</option>
              <option value="farmer">Farmers Only</option>
              <option value="customer">Customers Only</option>
            </select>
          </div>

          <div className="filter-group">
            <FontAwesomeIcon icon={faCalendarAlt} className="filter-icon" />
            <select
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setPage(1);
              }}
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Rated</option>
              <option value="lowest">Lowest Rated</option>
              <option value="helpful">Most Helpful</option>
            </select>
          </div>

          <div className="search-group">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search testimonials..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
            />
            {searchTerm && (
              <button
                className="clear-search"
                onClick={() => {
                  setSearchTerm("");
                  setPage(1);
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* ========== RATING DISTRIBUTION ========== */}
        {stats.byRating && stats.byRating.length > 0 && (
          <div className="rating-distribution">
            <h3>Rating Distribution</h3>
            <div className="rating-bars">
              {[5, 4, 3, 2, 1].map((rating) => {
                const ratingData = stats.byRating.find((r) => r._id === rating);
                const count = ratingData?.count || 0;
                const percentage = stats.total
                  ? (count / stats.total) * 100
                  : 0;
                return (
                  <div key={rating} className="rating-bar-item">
                    <div className="rating-label">{rating} ★</div>
                    <div className="rating-bar">
                      <div
                        className="rating-bar-fill"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <div className="rating-count">{count}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ========== TESTIMONIALS GRID ========== */}
        {loading && page === 1 ? (
          <Loader />
        ) : testimonials.length === 0 ? (
          <div className="no-testimonials">
            <FontAwesomeIcon icon={faQuoteLeft} />
            <h3>No testimonials found</h3>
            <p>Be the first to share your experience with our products!</p>
            {user && (
              <Button href="/dashboard" variant="primary">
                Share Your Experience <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            )}
            <a
              href="https://wa.me/23231030808"
              target="_blank"
              rel="noopener noreferrer"
              className="whatsapp-testimonial"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Contact Us on WhatsApp
            </a>
          </div>
        ) : (
          <>
            <div className="testimonials-grid">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={testimonial._id}
                  className="testimonial-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.05, 0.5) }}
                  whileHover={{ y: -5 }}
                >
                  <div className="testimonial-header">
                    <div className="user-info">
                      <div className="user-avatar">
                        {testimonial.userAvatar ? (
                          <img
                            src={testimonial.userAvatar}
                            alt={testimonial.userName}
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={getUserIcon(testimonial.userType)}
                          />
                        )}
                      </div>
                      <div className="user-details">
                        <h4>{testimonial.userName}</h4>
                        <span className="user-type">
                          <FontAwesomeIcon
                            icon={getUserIcon(testimonial.userType)}
                            className="user-type-icon"
                          />
                          {getUserTypeLabel(testimonial.userType)}
                        </span>
                      </div>
                    </div>
                    <div className="testimonial-rating">
                      {renderStars(testimonial.rating)}
                    </div>
                  </div>

                  <div className="testimonial-quote">
                    <FontAwesomeIcon icon={faQuoteLeft} />
                  </div>

                  <h3 className="testimonial-title">{testimonial.title}</h3>
                  <p className="testimonial-content">{testimonial.content}</p>

                  {testimonial.productName && (
                    <div className="testimonial-product">
                      <FontAwesomeIcon icon={faCheckCircle} />
                      <span>Product: {testimonial.productName}</span>
                    </div>
                  )}

                  <div className="testimonial-footer">
                    <div className="testimonial-date">
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span>{formatDate(testimonial.createdAt)}</span>
                    </div>
                    <button
                      className={`helpful-btn ${testimonial.helpfulCount > 0 ? "has-helpful" : ""}`}
                      onClick={() => handleMarkHelpful(testimonial._id)}
                      disabled={helping[testimonial._id]}
                    >
                      {helping[testimonial._id] ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        <FontAwesomeIcon icon={faThumbsUp} />
                      )}
                      <span>{testimonial.helpfulCount || 0}</span>
                    </button>
                  </div>

                  {testimonial.adminResponse && (
                    <div className="admin-response">
                      <strong>Response from Kenali Enterprise:</strong>
                      <p>{testimonial.adminResponse}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {hasMore && (
              <div className="load-more">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="load-more-btn"
                >
                  {loading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin /> Loading...
                    </>
                  ) : (
                    "Load More Reviews"
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* ========== CTA SECTION ========== */}
        {user ? (
          <div className="cta-section">
            <h3>Have you used our products?</h3>
            <p>
              Share your experience and help other farmers make informed
              decisions.
            </p>
            <Button href="/dashboard" variant="primary">
              Write a Testimonial <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </div>
        ) : (
          <div className="cta-section cta-guest">
            <h3>Share Your Experience</h3>
            <p>
              Login to share your testimonial and help other farmers make
              informed decisions.
            </p>
            <div className="cta-buttons">
              <Button href="/login" variant="primary">
                Login <FontAwesomeIcon icon={faArrowRight} />
              </Button>
              <Button href="/register" variant="outline-gold">
                Create Account
              </Button>
              <a
                href="https://wa.me/23231030808"
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-cta"
              >
                <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp Us
              </a>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .testimonials-hero {
          background: linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.forest} 100%);
          color: #ffffff;
          padding: 2.5rem 1.5rem;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .testimonials-hero { padding: 3.5rem 2rem; }
        }
        .testimonials-hero__overlay {
          position: absolute;
          top: -50%;
          right: -20%;
          width: 300px;
          height: 300px;
          background: rgba(217, 163, 33, 0.05);
          border-radius: 50%;
        }
        @media (min-width: 768px) {
          .testimonials-hero__overlay { width: 500px; height: 500px; }
        }
        .testimonials-hero__content { position: relative; z-index: 2; }
        .testimonials-hero__icon {
          font-size: 2rem;
          color: ${BRAND.secondary};
          margin-bottom: 0.75rem;
        }
        @media (min-width: 768px) {
          .testimonials-hero__icon { font-size: 2.5rem; margin-bottom: 1rem; }
        }
        .testimonials-hero h1 {
          font-size: clamp(1.75rem, 4vw, 2.5rem);
          margin-bottom: 0.5rem;
          font-family: "Montserrat", sans-serif;
        }
        .testimonials-hero p {
          font-size: 0.938rem;
          max-width: 600px;
          margin: 0 auto;
          opacity: 0.9;
          line-height: 1.7;
        }
        @media (min-width: 768px) {
          .testimonials-hero p { font-size: 1.063rem; }
        }
        .hero-stats {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          flex-wrap: wrap;
          margin-top: 1rem;
        }
        @media (min-width: 768px) {
          .hero-stats { gap: 3rem; margin-top: 1.5rem; }
        }
        .hero-stat { display: flex; flex-direction: column; align-items: center; }
        .hero-stat .stat-value {
          font-size: 1.25rem;
          font-weight: 700;
          font-family: "Montserrat", sans-serif;
          color: ${BRAND.secondary};
        }
        @media (min-width: 768px) {
          .hero-stat .stat-value { font-size: 1.75rem; }
        }
        .hero-stat .stat-label { font-size: 0.65rem; opacity: 0.8; }
        @media (min-width: 768px) {
          .hero-stat .stat-label { font-size: 0.75rem; }
        }

        .testimonials-container {
          max-width: 1280px;
          margin: 0 auto;
          padding: 1.5rem 1rem;
        }
        @media (min-width: 768px) {
          .testimonials-container { padding: 2rem; }
        }

        .testimonials-filters {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          padding: 1rem;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          margin-bottom: 1.5rem;
        }
        @media (min-width: 768px) {
          .testimonials-filters {
            flex-direction: row;
            gap: 1rem;
            padding: 1.25rem 1.5rem;
            margin-bottom: 2rem;
            align-items: center;
          }
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: ${BRAND.lightBg};
          border-radius: 8px;
          flex: 1;
        }
        @media (min-width: 768px) {
          .filter-group { padding: 0.5rem 1rem; flex: 0 1 auto; }
        }
        .filter-icon, .search-icon { color: #5A5A5A; font-size: 0.813rem; }
        .filter-group select {
          background: none;
          border: none;
          font-size: 0.813rem;
          padding: 0.25rem 0.5rem 0.25rem 0;
          outline: none;
          cursor: pointer;
          color: #1F1F1F;
          flex: 1;
          min-width: 100px;
          min-height: 44px;
          -webkit-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%235A5A5A' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right center;
          padding-right: 1.5rem;
        }
        @media (min-width: 768px) {
          .filter-group select { font-size: 0.875rem; }
        }
        .filter-group select:focus { color: ${BRAND.primary}; }

        .search-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 0.75rem;
          background: ${BRAND.lightBg};
          border-radius: 8px;
          flex: 1;
          position: relative;
        }
        @media (min-width: 768px) {
          .search-group { flex: 1; max-width: 300px; margin-left: auto; padding: 0.5rem 1rem; }
        }
        .search-group input {
          background: none;
          border: none;
          font-size: 0.813rem;
          padding: 0.25rem 0;
          outline: none;
          width: 100%;
          color: #1F1F1F;
          min-height: 44px;
        }
        @media (min-width: 768px) {
          .search-group input { font-size: 0.875rem; }
        }
        .search-group input::placeholder {
          color: #5A5A5A;
          opacity: 0.6;
        }
        .clear-search {
          background: none;
          border: none;
          color: #5A5A5A;
          cursor: pointer;
          padding: 0.25rem;
          font-size: 0.75rem;
          transition: color 0.3s ease;
          min-height: 44px;
        }
        .clear-search:hover { color: #dc2626; }

        .rating-distribution {
          background: #ffffff;
          padding: 1.25rem;
          border-radius: 12px;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        @media (min-width: 768px) {
          .rating-distribution { padding: 1.5rem 2rem; margin-bottom: 2rem; border-radius: 16px; }
        }
        .rating-distribution h3 {
          font-size: 0.938rem;
          margin-bottom: 1rem;
          color: #1F1F1F;
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .rating-distribution h3 { font-size: 1rem; }
        }

        .rating-bars {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .rating-bar-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        @media (min-width: 768px) {
          .rating-bar-item { gap: 1rem; }
        }
        .rating-label {
          width: 40px;
          font-size: 0.75rem;
          font-weight: 500;
          color: #1F1F1F;
        }
        @media (min-width: 768px) {
          .rating-label { width: 50px; font-size: 0.875rem; }
        }
        .rating-bar {
          flex: 1;
          height: 6px;
          background: #e5e5e5;
          border-radius: 3px;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .rating-bar { height: 8px; }
        }
        .rating-bar-fill {
          height: 100%;
          background: ${BRAND.secondary};
          border-radius: 3px;
          transition: width 0.5s ease;
        }
        .rating-count {
          width: 30px;
          font-size: 0.7rem;
          color: #5A5A5A;
          text-align: right;
        }
        @media (min-width: 768px) {
          .rating-count { width: 40px; font-size: 0.75rem; }
        }

        .testimonials-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        @media (min-width: 768px) {
          .testimonials-grid { grid-template-columns: repeat(2, 1fr); gap: 1.5rem; margin-bottom: 2rem; }
        }
        @media (min-width: 1024px) {
          .testimonials-grid { grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        }

        .testimonial-card {
          background: #ffffff;
          border-radius: 12px;
          padding: 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          border: 1px solid rgba(0, 0, 0, 0.04);
        }
        @media (min-width: 768px) {
          .testimonial-card { padding: 1.5rem; border-radius: 16px; }
        }
        .testimonial-card:hover {
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
          border-color: ${BRAND.secondary};
        }

        .testimonial-header {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1rem;
        }
        @media (min-width: 480px) {
          .testimonial-header { flex-direction: row; justify-content: space-between; align-items: flex-start; }
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }
        .user-avatar {
          width: 40px;
          height: 40px;
          min-width: 40px;
          background: linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.forest} 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 1rem;
          overflow: hidden;
        }
        @media (min-width: 768px) {
          .user-avatar { width: 48px; height: 48px; min-width: 48px; font-size: 1.25rem; }
        }
        .user-avatar img { width: 100%; height: 100%; object-fit: cover; }

        .user-details h4 {
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 0.125rem;
          color: #1F1F1F;
        }
        @media (min-width: 768px) {
          .user-details h4 { font-size: 1rem; }
        }
        .user-details .user-type {
          font-size: 0.65rem;
          color: #5A5A5A;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }
        @media (min-width: 768px) {
          .user-details .user-type { font-size: 0.7rem; }
        }
        .user-type-icon { font-size: 0.6rem; }

        .testimonial-rating { display: flex; gap: 0.125rem; flex-shrink: 0; }
        .testimonial-rating .star { font-size: 0.75rem; }
        @media (min-width: 768px) {
          .testimonial-rating .star { font-size: 0.875rem; }
        }
        .testimonial-rating .star.filled, .testimonial-rating .star.half-filled { color: ${BRAND.secondary}; }
        .testimonial-rating .star.empty { color: #e5e5e5; }

        .testimonial-quote { margin-bottom: 0.5rem; }
        @media (min-width: 768px) {
          .testimonial-quote { margin-bottom: 0.75rem; }
        }
        .testimonial-quote svg {
          font-size: 1.5rem;
          color: ${BRAND.secondary};
          opacity: 0.3;
        }
        @media (min-width: 768px) {
          .testimonial-quote svg { font-size: 2rem; }
        }

        .testimonial-title {
          font-size: 1rem;
          font-weight: 600;
          margin-bottom: 0.375rem;
          color: ${BRAND.primary};
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .testimonial-title { font-size: 1.1rem; }
        }

        .testimonial-content {
          color: #5A5A5A;
          line-height: 1.6;
          margin-bottom: 0.75rem;
          flex: 1;
          font-size: 0.813rem;
        }
        @media (min-width: 768px) {
          .testimonial-content { font-size: 0.875rem; margin-bottom: 1rem; }
        }

        .testimonial-product {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.65rem;
          color: ${BRAND.secondary};
          margin-bottom: 0.75rem;
          padding: 0.375rem 0.75rem;
          background: rgba(217, 163, 33, 0.08);
          border-radius: 8px;
        }
        @media (min-width: 768px) {
          .testimonial-product { font-size: 0.7rem; padding: 0.5rem 1rem; margin-bottom: 1rem; }
        }
        .testimonial-product svg { font-size: 0.6rem; }

        .testimonial-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 0.75rem;
          padding-top: 0.75rem;
          border-top: 1px solid #f0f0f0;
        }
        @media (min-width: 768px) {
          .testimonial-footer { margin-top: 1rem; padding-top: 1rem; }
        }

        .testimonial-date {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.65rem;
          color: #5A5A5A;
        }
        @media (min-width: 768px) {
          .testimonial-date { font-size: 0.7rem; gap: 0.5rem; }
        }

        .helpful-btn {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          background: none;
          border: none;
          color: #5A5A5A;
          font-size: 0.65rem;
          cursor: pointer;
          transition: all 0.3s ease;
          padding: 0.25rem 0.5rem;
          border-radius: 16px;
          font-family: "Inter", sans-serif;
          min-height: 36px;
        }
        @media (min-width: 768px) {
          .helpful-btn { font-size: 0.7rem; gap: 0.5rem; }
        }
        .helpful-btn:hover:not(:disabled) {
          color: ${BRAND.primary};
          background: ${BRAND.lightBg};
        }
        .helpful-btn.has-helpful { color: ${BRAND.primary}; }
        .helpful-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .admin-response {
          margin-top: 0.75rem;
          padding: 0.625rem 0.875rem;
          background: ${BRAND.lightBg};
          border-radius: 8px;
          border-left: 3px solid ${BRAND.secondary};
        }
        @media (min-width: 768px) {
          .admin-response { margin-top: 1rem; padding: 0.75rem 1rem; }
        }
        .admin-response strong {
          display: block;
          font-size: 0.65rem;
          color: #1F1F1F;
          margin-bottom: 0.125rem;
        }
        @media (min-width: 768px) {
          .admin-response strong { font-size: 0.7rem; }
        }
        .admin-response p {
          font-size: 0.75rem;
          color: #5A5A5A;
          margin: 0;
        }
        @media (min-width: 768px) {
          .admin-response p { font-size: 0.813rem; }
        }

        .load-more { text-align: center; margin: 1.5rem 0; }
        @media (min-width: 768px) {
          .load-more { margin: 2rem 0; }
        }
        .load-more-btn {
          padding: 0.625rem 1.5rem;
          background: #ffffff;
          border: 2px solid ${BRAND.secondary};
          color: ${BRAND.secondary};
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          font-size: 0.875rem;
          min-height: 44px;
          font-family: "Inter", sans-serif;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }
        @media (min-width: 768px) {
          .load-more-btn { padding: 0.75rem 2rem; font-size: 0.938rem; }
        }
        .load-more-btn:hover:not(:disabled) {
          background: ${BRAND.secondary};
          color: #ffffff;
          transform: translateY(-2px);
        }
        .load-more-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .no-testimonials {
          text-align: center;
          padding: 2rem 1.5rem;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }
        @media (min-width: 768px) {
          .no-testimonials { padding: 4rem 2rem; border-radius: 16px; }
        }
        .no-testimonials svg {
          font-size: 2.5rem;
          color: #5A5A5A;
          margin-bottom: 0.75rem;
        }
        @media (min-width: 768px) {
          .no-testimonials svg { font-size: 3rem; margin-bottom: 1rem; }
        }
        .no-testimonials h3 {
          margin-bottom: 0.5rem;
          color: #1F1F1F;
          font-family: "Montserrat", sans-serif;
        }
        .no-testimonials p {
          color: #5A5A5A;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
        }
        .whatsapp-testimonial {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1.25rem;
          background: #25d366;
          color: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          margin-top: 0.5rem;
        }
        .whatsapp-testimonial:hover {
          background: #1da851;
          transform: translateY(-2px);
        }

        .cta-section {
          text-align: center;
          padding: 1.5rem;
          background: linear-gradient(135deg, ${BRAND.primary} 0%, ${BRAND.forest} 100%);
          border-radius: 12px;
          margin: 1.5rem 0 1rem;
          color: #ffffff;
        }
        @media (min-width: 768px) {
          .cta-section { padding: 2.5rem 3rem; border-radius: 16px; margin: 2rem 0 2rem; }
        }
        .cta-section h3 {
          font-size: 1.125rem;
          margin-bottom: 0.375rem;
          font-family: "Montserrat", sans-serif;
        }
        @media (min-width: 768px) {
          .cta-section h3 { font-size: 1.5rem; margin-bottom: 0.5rem; }
        }
        .cta-section p {
          opacity: 0.9;
          margin-bottom: 1rem;
          font-size: 0.875rem;
        }
        @media (min-width: 768px) {
          .cta-section p { font-size: 1rem; margin-bottom: 1.5rem; }
        }
        .cta-section .btn-primary {
          background: ${BRAND.secondary};
          color: #ffffff;
        }
        .cta-section .btn-primary:hover { background: ${BRAND.goldHover}; }

        .cta-guest .cta-buttons {
          display: flex;
          gap: 0.75rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        @media (min-width: 768px) {
          .cta-guest .cta-buttons { gap: 1rem; }
        }
        .cta-guest .btn-outline-gold {
          border-color: ${BRAND.secondary};
          color: ${BRAND.secondary};
          background: transparent;
        }
        .cta-guest .btn-outline-gold:hover {
          background: ${BRAND.secondary};
          color: #ffffff;
        }
        .whatsapp-cta {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.625rem 1.5rem;
          background: #25d366;
          color: #ffffff;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
        }
        .whatsapp-cta:hover {
          background: #1da851;
          transform: translateY(-2px);
        }

        @media (max-width: 576px) {
          .testimonials-container { padding: 1rem 0.75rem; }
          .testimonial-card { padding: 1rem; }
          .testimonial-header { flex-direction: column; gap: 0.5rem; }
          .testimonial-rating { align-self: flex-start; }
          .cta-section { padding: 1.25rem; margin: 1rem 0 0.5rem; }
          .cta-section h3 { font-size: 1rem; }
          .filter-group select { font-size: 0.75rem; }
          .search-group input { font-size: 0.75rem; }
        }
      `}</style>
    </motion.div>
    </>
  );
};

export default Testimonials;
